import {Device, Call} from '@twilio/voice-sdk';
import cx from 'classnames';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {sessionApi} from 'api';
import {Button, DataWrapper, notifier, Tooltip} from 'components';
import {usePrevious, useTwilioDevice, useUserSession} from 'hooks';
import {SessionActivityVoiceAgent, VoiceAgentMode} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {ParticipantRole} from 'types';
import {userAuthUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.COMPONENTS.SESSION_VOICE;
const VOICE_AGENT_T_KEYS = TRANSLATION_KEYS.COMPONENTS.SESSION_VOICE_AGENT;

type Props = {
    sessionId: string;
    conference: {
        name: string;
    },
    audioEnabled: boolean;
    voiceAgent?: SessionActivityVoiceAgent;
}

const VoiceAgentToggle: React.FC<{
    sessionId: string;
    voiceAgent: SessionActivityVoiceAgent;
}> = ({sessionId, voiceAgent}) => {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(false);
    const [mode, setMode] = useState<VoiceAgentMode>(voiceAgent.currentMode);

    const aiActive = useMemo(() => mode === VoiceAgentMode.AI, [mode]);

    const switchToHuman = useCallback(async () => {
        if (mode === VoiceAgentMode.Human || loading) {
            return;
        }
        setLoading(true);
        try {
            await sessionApi.switchSipMode(sessionId, VoiceAgentMode.Human);
            setMode(VoiceAgentMode.Human);
        } catch {
            notifier.error({title: t(VOICE_AGENT_T_KEYS.ERROR.SWITCH_FAILED)});
        } finally {
            setLoading(false);
        }
    }, [sessionId, loading, mode, t]);

    useEffect(() => {
        setMode(voiceAgent.currentMode);
    }, [voiceAgent.currentMode]);

    return (
        <div className='tw-flex tw-items-center tw-gap-2'>
            <Tooltip message={t(VOICE_AGENT_T_KEYS.AI_DISABLED_TOOLTIP)}>
                <Button
                    color={aiActive ? 'primary' : 'default'}
                    disabled
                    size='small'
                    className={cx({'tw-opacity-60': !aiActive})}
                >
                    {t(VOICE_AGENT_T_KEYS.MODES.AI)}
                </Button>
            </Tooltip>
            <Button
                color={aiActive ? 'default' : 'primary'}
                loading={loading}
                size='small'
                onClick={switchToHuman}
            >
                {t(VOICE_AGENT_T_KEYS.MODES.HUMAN)}
            </Button>
        </div>
    );
};

export const TwilioSip: React.FC<Props> = ({sessionId, conference, audioEnabled, voiceAgent}) => {
    const {t} = useTranslation();
    const localUserEid = useMemo(() => userAuthUtils.getLocalUser().eid, []);
    const {device, setDevice, init} = useTwilioDevice(ParticipantRole.Agent);
    const [twilioCall, setTwilioCall] = useState<Call>(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const shouldCall = useMemo(() => !error && !!device && !twilioCall, [error, device, twilioCall]);
    const prevShouldCall = usePrevious(shouldCall);

    const {clientParticipant} = useUserSession();

    const humanMode = useMemo(() => voiceAgent?.currentMode === VoiceAgentMode.Human, [voiceAgent]);
    const shouldConnectDevice = useMemo(
        () => !!conference?.name && (!voiceAgent || humanMode),
        [conference, voiceAgent, humanMode]
    );

    const connectOptions: Device.ConnectOptions = useMemo(() => ({
        params: {
            ConferenceName: conference?.name,
            UserEid: localUserEid.toString()
        }
    }), [conference, localUserEid]);

    useEffect(() => {
        if (shouldConnectDevice && shouldCall && !prevShouldCall) {
            setLoading(true);
            device.connect(connectOptions).then((activeCall) => {
                setTwilioCall(activeCall);
            }).catch((e: any) => {
                // eslint-disable-next-line no-console
                console.log(e);
                if (e.name !== 'InvalidStateError') {
                    setError(e);
                }
            }).finally(() => setLoading(false));
        }
    }, [shouldCall, device, setTwilioCall, connectOptions, prevShouldCall, shouldConnectDevice]);

    useEffect(() => {
        if (!shouldConnectDevice) {
            return;
        }
        if (Device.isSupported) {
            if (!device && !clientParticipant.left) {
                init();
            }
        } else {
            notifier.error({title: t(T_KEYS.ERROR.NOT_SUPPORTED)});
        }
    }, [device, init, t, clientParticipant, shouldConnectDevice]);

    useEffect(() => {
        if (twilioCall) {
            if (!audioEnabled && !twilioCall.isMuted()) {
                twilioCall.mute(true);
            } else if (audioEnabled && twilioCall.isMuted()) {
                twilioCall.mute(false);
            }
        }
    }, [audioEnabled, twilioCall]);

    useEffect(() => () => {
        if (device) {
            device.disconnectAll();
            device.destroy();
            setDevice(null);
        }
    }, [device, setDevice]);

    return (
        <div className='tw-relative tw-w-16 tw-h-16'>
            {voiceAgent && <VoiceAgentToggle sessionId={sessionId} voiceAgent={voiceAgent} />}
            <DataWrapper isLoading={loading} isError={!!error} />
        </div>

    );
};
